/**
 * NetworkGraph
 *
 * D3 force-directed network visualization
 * - Canvas 렌더링 (1000+ 노드 성능)
 * - 줌/팬/드래그 지원
 * - 호버 툴팁
 *
 * @param {Object} data - { nodes: [], links: [] }
 * @param {number} minWeight - 링크 최소 weight (기본: 20)
 * @param {number} height - 높이 (기본: 600)
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Slider, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as d3 from 'd3';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useForceSimulation } from './hooks/useForceSimulation';
import NetworkTooltip from './NetworkTooltip';

const NODE_RADIUS = 20;

/**
 * username을 색상으로 변환
 */
const stringToColor = (str) => {
  if (!str) return '#888';
  const s = String(str);
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 60%, 50%)`;
};

const NetworkGraph = ({
  data,
  minWeight: initialMinWeight = 20,
  height: propHeight = 600,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile('md');
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  // 상태
  const [dimensions, setDimensions] = useState({ width: 800, height: propHeight });
  const [transform, setTransform] = useState(d3.zoomIdentity);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [minWeight, setMinWeight] = useState(initialMinWeight);
  
  // 반응형 설정
  const config = useMemo(() => ({
    nodeRadius: isMobile ? 14 : NODE_RADIUS,
    height: isMobile ? 450 : propHeight,
    defaultMinWeight: isMobile ? 50 : 20,
  }), [isMobile, propHeight]);

  // 시뮬레이션
  const {
    nodes,
    links,
    isStable,
    restart,
    stop,
    fixNode,
    releaseNode,
  } = useForceSimulation(
    data?.network?.nodes || [],
    data?.network?.links || [],
    { width: dimensions.width, height: config.height, minWeight }
  );

  // 컨테이너 크기 감지
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(entries => {
      const { width } = entries[0].contentRect;
      setDimensions({ width, height: config.height });
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [config.height]);

  // 이미지 프리로드 비활성화 (성능 최적화)
  // 필요시 showAvatars prop으로 활성화 가능

  // Canvas 렌더링
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !nodes.length) return;

    const ctx = canvas.getContext('2d');
    const { width, height } = dimensions;
    const radius = config.nodeRadius;

    // 캔버스 크기 설정 (HiDPI 지원)
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // 렌더 함수
    const render = () => {
      ctx.save();
      ctx.clearRect(0, 0, width, height);

      // Transform 적용
      ctx.translate(transform.x, transform.y);
      ctx.scale(transform.k, transform.k);

      // 링크 렌더링
      links.forEach(link => {
        const source = link.source;
        const target = link.target;

        if (!source.x || !target.x) return;

        const opacity = Math.min(link.weight / 300, 0.6);
        const lineWidth = Math.max(0.5, Math.log(link.weight) / 3);

        ctx.beginPath();
        ctx.moveTo(source.x, source.y);
        ctx.lineTo(target.x, target.y);
        ctx.strokeStyle = theme.palette.mode === 'dark'
          ? `rgba(255, 255, 255, ${opacity})`
          : `rgba(100, 100, 100, ${opacity})`;
        ctx.lineWidth = lineWidth / transform.k;
        ctx.stroke();
      });

      // 노드 렌더링
      nodes.forEach(node => {
        if (!node.x || !node.y) return;

        const isHovered = hoveredNode?.id === node.id;
        const r = isHovered ? radius * 1.3 : radius;

        // 색상 원
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
        ctx.fillStyle = stringToColor(node.username);
        ctx.fill();

        // 이니셜
        ctx.fillStyle = '#fff';
        ctx.font = `bold ${r * 0.8}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const initial = node.username ? String(node.username).charAt(0).toUpperCase() : '?';
        ctx.fillText(initial, node.x, node.y);

        // 테두리
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
        ctx.strokeStyle = isHovered
          ? theme.palette.primary.main
          : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)';
        ctx.lineWidth = isHovered ? 3 / transform.k : 1.5 / transform.k;
        ctx.stroke();
      });

      ctx.restore();
    };

    render();
  }, [nodes, links, dimensions, transform, hoveredNode, config.nodeRadius, theme]);

  // 줌/팬 설정
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const zoom = d3.zoom()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => {
        setTransform(event.transform);
      });

    d3.select(canvas).call(zoom);

    return () => {
      d3.select(canvas).on('.zoom', null);
    };
  }, []);

  // 마우스 이벤트 (hit detection)
  const findNodeAtPoint = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const x = (clientX - rect.left - transform.x) / transform.k;
    const y = (clientY - rect.top - transform.y) / transform.k;

    return nodes.find(node => {
      if (!node.x || !node.y) return false;
      const dx = node.x - x;
      const dy = node.y - y;
      return Math.sqrt(dx * dx + dy * dy) < config.nodeRadius;
    });
  }, [nodes, transform, config.nodeRadius]);

  const handleMouseMove = useCallback((event) => {
    const node = findNodeAtPoint(event.clientX, event.clientY);
    setHoveredNode(node);

    if (node) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPos({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    } else {
      setTooltipPos(null);
    }
  }, [findNodeAtPoint]);

  const handleClick = useCallback((event) => {
    const node = findNodeAtPoint(event.clientX, event.clientY);
    if (node?.link) {
      window.open(node.link, '_blank', 'noopener,noreferrer');
    }
  }, [findNodeAtPoint]);

  // 드래그
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let draggedNode = null;

    const drag = d3.drag()
      .container(canvas)
      .subject((event) => {
        const node = findNodeAtPoint(event.sourceEvent.clientX, event.sourceEvent.clientY);
        return node || null;
      })
      .on('start', (event) => {
        draggedNode = event.subject;
        if (draggedNode) {
          restart(0.3);
          fixNode(draggedNode, draggedNode.x, draggedNode.y);
        }
      })
      .on('drag', (event) => {
        if (draggedNode) {
          const x = (event.sourceEvent.clientX - canvasRef.current.getBoundingClientRect().left - transform.x) / transform.k;
          const y = (event.sourceEvent.clientY - canvasRef.current.getBoundingClientRect().top - transform.y) / transform.k;
          fixNode(draggedNode, x, y);
        }
      })
      .on('end', () => {
        if (draggedNode) {
          stop();
          releaseNode(draggedNode);
          draggedNode = null;
        }
      });

    d3.select(canvas).call(drag);

    return () => {
      d3.select(canvas).on('.drag', null);
    };
  }, [findNodeAtPoint, transform, restart, stop, fixNode, releaseNode]);

  if (!data?.network?.nodes?.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No network data available</Typography>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
      {/* 필터 컨트롤 */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mb: 2, px: 1 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
          Min Weight: {minWeight}
        </Typography>
        <Slider
          value={minWeight}
          onChange={(_, value) => setMinWeight(value)}
          min={1}
          max={200}
          step={5}
          size="small"
          sx={{ maxWidth: 200 }}
        />
        <Typography variant="caption" color="text.secondary">
          {links.length} links / {nodes.length} nodes
        </Typography>
      </Stack>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          cursor: hoveredNode ? 'pointer' : 'grab',
          borderRadius: 8,
          backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fafafa',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => { setHoveredNode(null); setTooltipPos(null); }}
        onClick={handleClick}
      />

      {/* 상태 표시 */}
      {!isStable && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            color: 'text.secondary',
            bgcolor: 'background.paper',
            px: 1,
            py: 0.5,
            borderRadius: 1,
          }}
        >
          Stabilizing...
        </Typography>
      )}

      {/* 툴팁 */}
      {hoveredNode && tooltipPos && (
        <NetworkTooltip node={hoveredNode} position={tooltipPos} />
      )}
    </Box>
  );
};

export default NetworkGraph;
